import { DEFAULT_DATE_FORMAT } from '@shared/utils'
import dayjs from 'dayjs'

/**
 * Notification messages are authored as Django templates on the backend, so
 * `message_subject` / `message_body` reach the dashboard with the placeholders
 * still in place ("Polița {{ product_name }} #{{ policy_number }} a fost
 * emisă"). Every notification also carries the `context` used when it was
 * distributed, which is enough to render the message the recipient received.
 *
 * Supported subset: `{{ variable.path|filter:"arg" }}`, `{% if %}` /
 * `{% elif %}` / `{% else %}` / `{% endif %}`, `{% for %}` / `{% empty %}` /
 * `{% endfor %}` and `{# comments #}`. Unsupported tags render as nothing.
 */

export type TemplateContext = Record<string, unknown> | null | undefined

export type RenderTemplateOptions = {
  /** Escape the interpolated values, like Django's autoescape. Default `true`. */
  autoescape?: boolean
  /** Drop the line left behind by a tag that sits alone on it. Default `true`. */
  trimBlocks?: boolean
  /** Rendered instead of a variable missing from the context. Default `''`. */
  fallback?: string
}

type Settings = Required<RenderTemplateOptions>

type Scope = Record<string, unknown>

type EscapeState = 'auto' | 'safe' | 'escape'

type Evaluated = { value: unknown; state: EscapeState }

type Token =
  | { type: 'text'; value: string }
  | { type: 'variable'; value: string }
  | { type: 'tag'; value: string }
  | { type: 'comment'; value: string }

type Branch = { condition: string; body: Node[] }

type Node =
  | { type: 'text'; value: string }
  | { type: 'variable'; expression: string }
  | { type: 'if'; branches: Branch[]; alternate: Node[] }
  | {
      type: 'for'
      names: string[]
      source: string
      reversed: boolean
      body: Node[]
      empty: Node[]
    }

type ParseResult = {
  nodes: Node[]
  index: number
  stopper?: string
  stopperArgs: string
}

const TOKEN_PATTERN = /(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{#[\s\S]*?#\})/
const TEMPLATE_SYNTAX = /\{[{%#]/
const QUOTED = /^(['"])([\s\S]*)\1$/
const NUMERIC = /^-?\d+(\.\d+)?$/
const INDEX_KEY = /^\d+$/
const LINE_START = /(^|\n)[ \t]*$/
const LINE_BREAK = /^[ \t]*\r?\n/

const COMPARISONS = ['==', '!=', '<', '>', '<=', '>=', 'in', 'is']

/* -------------------------------------------------------------------------- */
/* values                                                                     */
/* -------------------------------------------------------------------------- */

const isTruthy = (value: unknown): boolean => {
  if (value === null || value === undefined || value === false) return false
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return value.length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0

  return Boolean(value)
}

const stringify = (value: unknown, fallback: string): string => {
  if (value === null || value === undefined) return fallback
  if (typeof value === 'boolean') return value ? 'True' : 'False'

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value) ?? fallback
    } catch {
      return fallback
    }
  }

  return String(value)
}

const asString = (value: unknown) => stringify(value, '')

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const compare = (left: unknown, right: unknown) => {
  if (typeof left === typeof right) return left === right
  if (left === null || right === null) return left === right
  if (typeof left === 'object' || typeof right === 'object') return false

  return asString(left) === asString(right)
}

const contains = (collection: unknown, value: unknown) => {
  if (Array.isArray(collection))
    return collection.some((item) => compare(item, value))
  if (typeof collection === 'string')
    return collection.includes(asString(value))
  if (collection && typeof collection === 'object')
    return Object.keys(collection).includes(asString(value))

  return false
}

/* -------------------------------------------------------------------------- */
/* filters                                                                    */
/* -------------------------------------------------------------------------- */

const DATE_TOKENS: Record<string, string> = {
  A: 'A',
  D: 'ddd',
  F: 'MMMM',
  G: 'H',
  H: 'HH',
  M: 'MMM',
  N: 'MMM',
  O: 'ZZ',
  P: 'h:mm a',
  T: 'z',
  U: 'X',
  Y: 'YYYY',
  a: 'a',
  d: 'DD',
  e: 'z',
  g: 'h',
  h: 'hh',
  i: 'mm',
  j: 'D',
  l: 'dddd',
  m: 'MM',
  n: 'M',
  s: 'ss',
  u: 'SSS',
  w: 'd',
  y: 'YY'
}

const toDayjsFormat = (format: string) => {
  let result = ''

  for (let index = 0; index < format.length; index += 1) {
    const char = format[index]

    if (char === '\\') {
      result += `[${format[index + 1] ?? ''}]`
      index += 1
      continue
    }

    result += DATE_TOKENS[char] ?? `[${char}]`
  }

  return result
}

const formatDate = (
  value: unknown,
  argument: unknown,
  fallbackFormat: string
) => {
  if (value === null || value === undefined || value === '') return value

  const date = dayjs(value as dayjs.ConfigType)

  if (!date.isValid()) return value

  return date.format(
    argument ? toDayjsFormat(asString(argument)) : fallbackFormat
  )
}

const floatFormat = (value: unknown, argument: unknown) => {
  const amount = Number(value)

  if (Number.isNaN(amount)) return value

  const digits = argument === undefined ? -1 : Number(argument)

  if (Number.isNaN(digits)) return amount

  if (digits >= 0) return amount.toFixed(digits)

  const rounded = amount.toFixed(Math.abs(digits))

  return Number(rounded) === Math.trunc(amount)
    ? String(Math.trunc(amount))
    : rounded
}

const toLength = (value: unknown) => {
  if (Array.isArray(value) || typeof value === 'string') return value.length
  if (value && typeof value === 'object') return Object.keys(value).length

  return 0
}

const FILTERS: Record<string, (value: unknown, argument: unknown) => unknown> =
  {
    add: (value, argument) => {
      const left = Number(value)
      const right = Number(argument)

      if (Number.isNaN(left) || Number.isNaN(right))
        return `${asString(value)}${asString(argument)}`

      return left + right
    },
    capfirst: (value) => {
      const text = asString(value)

      return text ? text[0].toUpperCase() + text.slice(1) : text
    },
    cut: (value, argument) =>
      asString(value).split(asString(argument)).join(''),
    date: (value, argument) => formatDate(value, argument, DEFAULT_DATE_FORMAT),
    default: (value, argument) => (isTruthy(value) ? value : argument),
    default_if_none: (value, argument) =>
      value === null || value === undefined ? argument : value,
    first: (value) => (Array.isArray(value) ? value[0] : asString(value)[0]),
    floatformat: (value, argument) => floatFormat(value, argument),
    join: (value, argument) =>
      Array.isArray(value)
        ? value
            .map(asString)
            .join(argument === undefined ? ', ' : asString(argument))
        : value,
    last: (value) =>
      Array.isArray(value)
        ? value[value.length - 1]
        : asString(value).slice(-1),
    length: (value) => toLength(value),
    linebreaksbr: (value) =>
      escapeHtml(asString(value)).replace(/\r?\n/g, '<br />'),
    lower: (value) => asString(value).toLowerCase(),
    striptags: (value) => asString(value).replace(/<[^>]*>/g, ''),
    time: (value, argument) => formatDate(value, argument ?? 'H:i', 'HH:mm'),
    title: (value) =>
      asString(value).replace(
        /\w\S*/g,
        (word) => word[0].toUpperCase() + word.slice(1)
      ),
    truncatechars: (value, argument) => {
      const text = asString(value)
      const limit = Number(argument)

      return Number.isNaN(limit) || text.length <= limit
        ? text
        : `${text.slice(0, limit)}…`
    },
    upper: (value) => asString(value).toUpperCase(),
    yesno: (value, argument) => {
      const [yes = 'yes', no = 'no', maybe] = asString(
        argument || 'yes,no'
      ).split(',')

      if (value === null || value === undefined) return maybe ?? no

      return isTruthy(value) ? yes : no
    }
  }

/** Filters whose output is HTML and must not be escaped afterwards. */
const SAFE_FILTERS = ['linebreaksbr']

/* -------------------------------------------------------------------------- */
/* expressions                                                                */
/* -------------------------------------------------------------------------- */

const splitUnquoted = (source: string, separator: string, limit = Infinity) => {
  const parts: string[] = []
  let current = ''
  let quote: string | null = null

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]

    if (quote) {
      current += char
      if (char === quote && source[index - 1] !== '\\') quote = null
      continue
    }

    if (char === '"' || char === "'") {
      quote = char
      current += char
      continue
    }

    if (char === separator && parts.length < limit) {
      parts.push(current)
      current = ''
      continue
    }

    current += char
  }

  parts.push(current)

  return parts
}

const resolvePath = (path: string, scope: Scope): unknown =>
  path.split('.').reduce<unknown>((current, key) => {
    if (current === null || current === undefined) return undefined

    if (Array.isArray(current)) {
      if (INDEX_KEY.test(key)) return current[Number(key)]
      if (key === 'first') return current[0]
      if (key === 'last') return current[current.length - 1]

      return undefined
    }

    if (typeof current === 'object') {
      const record = current as Scope

      if (key === 'items' && !(key in record)) return Object.entries(record)

      return record[key]
    }

    if (typeof current === 'string' && INDEX_KEY.test(key))
      return current[Number(key)]

    return undefined
  }, scope)

const evaluateAtom = (source: string, scope: Scope): unknown => {
  const atom = source.trim()

  if (!atom) return undefined

  const quoted = QUOTED.exec(atom)

  if (quoted) return quoted[2]
  if (NUMERIC.test(atom)) return Number(atom)
  if (atom === 'True' || atom === 'true') return true
  if (atom === 'False' || atom === 'false') return false
  if (atom === 'None' || atom === 'null') return null

  return resolvePath(atom, scope)
}

const evaluateExpression = (source: string, scope: Scope): Evaluated => {
  const [head, ...filters] = splitUnquoted(source, '|')

  let value = evaluateAtom(head, scope)
  let state: EscapeState = 'auto'

  filters.forEach((filter) => {
    const [rawName, rawArgument] = splitUnquoted(filter.trim(), ':', 1)
    const name = rawName.trim()

    if (name === 'safe') {
      state = 'safe'
      return
    }

    if (name === 'escape' || name === 'force_escape') {
      state = 'escape'
      return
    }

    const apply = FILTERS[name]

    if (!apply) return

    value = apply(
      value,
      rawArgument === undefined ? undefined : evaluateAtom(rawArgument, scope)
    )

    if (SAFE_FILTERS.includes(name)) state = 'safe'
  })

  return { value, state }
}

const tokenizeCondition = (source: string) => {
  const tokens: string[] = []
  let index = 0

  while (index < source.length) {
    const char = source[index]

    if (/\s/.test(char)) {
      index += 1
      continue
    }

    if (char === '(' || char === ')') {
      tokens.push(char)
      index += 1
      continue
    }

    const pair = source.slice(index, index + 2)

    if (['==', '!=', '<=', '>='].includes(pair)) {
      tokens.push(pair)
      index += 2
      continue
    }

    if (char === '<' || char === '>') {
      tokens.push(char)
      index += 1
      continue
    }

    let atom = ''

    while (index < source.length) {
      const current = source[index]

      if (/\s/.test(current) || current === '(' || current === ')') break

      if (current === '"' || current === "'") {
        const closing = source.indexOf(current, index + 1)
        const end = closing === -1 ? source.length : closing

        atom += source.slice(index, end + 1)
        index = end + 1
        continue
      }

      if (
        current === '<' ||
        current === '>' ||
        ['==', '!='].includes(source.slice(index, index + 2))
      )
        break

      atom += current
      index += 1
    }

    if (atom) tokens.push(atom)
    else index += 1
  }

  return tokens
}

const evaluateCondition = (source: string, scope: Scope): boolean => {
  const tokens = tokenizeCondition(source)
  let cursor = 0

  const parsePrimary = (): unknown => {
    const token = tokens[cursor]

    if (token === '(') {
      cursor += 1

      const value = parseOr()

      if (tokens[cursor] === ')') cursor += 1

      return value
    }

    cursor += 1

    return evaluateExpression(token ?? '', scope).value
  }

  const parseComparison = (): unknown => {
    const left = parsePrimary()
    const operator = tokens[cursor]

    if (operator === 'not' && tokens[cursor + 1] === 'in') {
      cursor += 2

      return !contains(parsePrimary(), left)
    }

    if (!operator || !COMPARISONS.includes(operator)) return left

    cursor += 1

    if (operator === 'is' && tokens[cursor] === 'not') {
      cursor += 1

      return !compare(left, parsePrimary())
    }

    const right = parsePrimary()

    switch (operator) {
      case '==':
      case 'is':
        return compare(left, right)
      case '!=':
        return !compare(left, right)
      case '<':
        return Number(left) < Number(right)
      case '>':
        return Number(left) > Number(right)
      case '<=':
        return Number(left) <= Number(right)
      case '>=':
        return Number(left) >= Number(right)
      default:
        return contains(right, left)
    }
  }

  const parseNot = (): unknown => {
    if (tokens[cursor] !== 'not') return parseComparison()

    cursor += 1

    return !isTruthy(parseNot())
  }

  const parseAnd = (): unknown => {
    let value = parseNot()

    while (tokens[cursor] === 'and') {
      cursor += 1
      value = isTruthy(parseNot()) && isTruthy(value)
    }

    return value
  }

  const parseOr = (): unknown => {
    let value = parseAnd()

    while (tokens[cursor] === 'or') {
      cursor += 1
      value = isTruthy(parseAnd()) || isTruthy(value)
    }

    return value
  }

  return isTruthy(parseOr())
}

/* -------------------------------------------------------------------------- */
/* parser                                                                     */
/* -------------------------------------------------------------------------- */

const tokenize = (template: string): Token[] =>
  template
    .split(TOKEN_PATTERN)
    .filter((part) => !!part)
    .map((part): Token => {
      if (part.startsWith('{{'))
        return { type: 'variable', value: part.slice(2, -2).trim() }
      if (part.startsWith('{%'))
        return { type: 'tag', value: part.slice(2, -2).trim() }
      if (part.startsWith('{#'))
        return { type: 'comment', value: part.slice(2, -2).trim() }

      return { type: 'text', value: part }
    })

/** Removes the blank line a `{% if %}` / `{% endif %}` leaves behind. */
const trimStandaloneTags = (tokens: Token[]) => {
  tokens.forEach((token, index) => {
    if (token.type !== 'tag' && token.type !== 'comment') return

    const previous = tokens[index - 1]
    const next = tokens[index + 1]
    const startsLine =
      !previous || (previous.type === 'text' && LINE_START.test(previous.value))
    const endsLine =
      !next || (next.type === 'text' && LINE_BREAK.test(next.value))

    if (!startsLine || !endsLine) return

    if (previous?.type === 'text')
      previous.value = previous.value.replace(LINE_START, '$1')
    if (next?.type === 'text') next.value = next.value.replace(LINE_BREAK, '')
  })
}

const splitTag = (value: string): [string, string] => {
  const match = /^(\S+)\s*([\s\S]*)$/.exec(value)

  return match ? [match[1], match[2].trim()] : ['', '']
}

const parseNodes = (
  tokens: Token[],
  start: number,
  boundaries: string[]
): ParseResult => {
  const nodes: Node[] = []
  let index = start

  while (index < tokens.length) {
    const token = tokens[index]

    if (token.type === 'text') {
      nodes.push({ type: 'text', value: token.value })
      index += 1
      continue
    }

    if (token.type === 'variable') {
      nodes.push({ type: 'variable', expression: token.value })
      index += 1
      continue
    }

    if (token.type === 'comment') {
      index += 1
      continue
    }

    const [name, args] = splitTag(token.value)

    if (boundaries.includes(name))
      return { nodes, index, stopper: name, stopperArgs: args }

    if (name === 'if') {
      const branches: Branch[] = []
      let condition = args
      let alternate: Node[] = []
      let cursor = index + 1

      for (;;) {
        const branch = parseNodes(tokens, cursor, ['elif', 'else', 'endif'])

        if (!branch.stopper) throw new Error('Missing {% endif %}')

        branches.push({ condition, body: branch.nodes })
        cursor = branch.index + 1

        if (branch.stopper === 'elif') {
          condition = branch.stopperArgs
          continue
        }

        if (branch.stopper === 'else') {
          const otherwise = parseNodes(tokens, cursor, ['endif'])

          if (!otherwise.stopper) throw new Error('Missing {% endif %}')

          alternate = otherwise.nodes
          cursor = otherwise.index + 1
        }

        break
      }

      nodes.push({ type: 'if', branches, alternate })
      index = cursor
      continue
    }

    if (name === 'for') {
      const loop = parseNodes(tokens, index + 1, ['empty', 'endfor'])
      let empty: Node[] = []
      let cursor = loop.index + 1

      if (!loop.stopper) throw new Error('Missing {% endfor %}')

      if (loop.stopper === 'empty') {
        const fallback = parseNodes(tokens, cursor, ['endfor'])

        if (!fallback.stopper) throw new Error('Missing {% endfor %}')

        empty = fallback.nodes
        cursor = fallback.index + 1
      }

      const match = /^([\s\S]+?)\s+in\s+([\s\S]+?)(\s+reversed)?$/.exec(args)

      nodes.push({
        type: 'for',
        names: match ? match[1].split(',').map((part) => part.trim()) : [],
        source: match ? match[2].trim() : '',
        reversed: !!match?.[3],
        body: loop.nodes,
        empty
      })
      index = cursor
      continue
    }

    // Unsupported tag ({% load %}, {% csrf_token %}, ...) renders nothing.
    index += 1
  }

  return { nodes, index, stopperArgs: '' }
}

/* -------------------------------------------------------------------------- */
/* renderer                                                                   */
/* -------------------------------------------------------------------------- */

const toIterable = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object') return Object.keys(value)

  return []
}

const unpack = (names: string[], item: unknown): Scope => {
  if (names.length <= 1) return { [names[0] || 'item']: item }

  const values = Array.isArray(item) ? item : [item]

  return names.reduce<Scope>(
    (scope, name, index) => ({ ...scope, [name]: values[index] }),
    {}
  )
}

const renderNodes = (nodes: Node[], scope: Scope, settings: Settings): string =>
  nodes
    .map((node) => {
      if (node.type === 'text') return node.value

      if (node.type === 'variable') {
        const { value, state } = evaluateExpression(node.expression, scope)
        const text = stringify(value, settings.fallback)
        const shouldEscape =
          state === 'escape' || (state === 'auto' && settings.autoescape)

        return shouldEscape ? escapeHtml(text) : text
      }

      if (node.type === 'if') {
        const branch = node.branches.find(({ condition }) =>
          evaluateCondition(condition, scope)
        )

        return renderNodes(
          branch ? branch.body : node.alternate,
          scope,
          settings
        )
      }

      const items = toIterable(evaluateExpression(node.source, scope).value)
      const list = node.reversed ? [...items].reverse() : items

      if (!list.length) return renderNodes(node.empty, scope, settings)

      return list
        .map((item, index) =>
          renderNodes(
            node.body,
            {
              ...scope,
              ...unpack(node.names, item),
              forloop: {
                counter: index + 1,
                counter0: index,
                first: index === 0,
                last: index === list.length - 1,
                revcounter: list.length - index,
                revcounter0: list.length - index - 1
              }
            },
            settings
          )
        )
        .join('')
    })
    .join('')

/* -------------------------------------------------------------------------- */
/* public api                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Fills the Django placeholders of a notification message with the values of
 * its context. Returns the template untouched when it cannot be rendered, so a
 * malformed template never breaks the screen displaying it.
 */
export const renderTemplate = (
  template?: string | null,
  context?: TemplateContext,
  options?: RenderTemplateOptions
): string => {
  if (!template) return ''
  if (!TEMPLATE_SYNTAX.test(template)) return template

  const scope = context && typeof context === 'object' ? (context as Scope) : {}

  // Nothing to fill the placeholders with - the raw template is more helpful
  // than a message with holes in it.
  if (!Object.keys(scope).length) return template

  const settings: Settings = {
    autoescape: options?.autoescape ?? true,
    trimBlocks: options?.trimBlocks ?? true,
    fallback: options?.fallback ?? ''
  }

  try {
    const tokens = tokenize(template)

    if (settings.trimBlocks) trimStandaloneTags(tokens)

    return renderNodes(parseNodes(tokens, 0, []).nodes, scope, settings)
  } catch {
    return template
  }
}

/**
 * Same as {@link renderTemplate}, but without any markup - for tooltips, table
 * cells or anywhere the message is displayed as plain text.
 */
export const renderTemplateAsText = (
  template?: string | null,
  context?: TemplateContext,
  options?: RenderTemplateOptions
): string =>
  renderTemplate(template, context, { ...options, autoescape: false })
    .replace(/<[^>]*>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
